import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis, Cell,
} from "recharts";
import { Phone, EnvelopeSimple, CalendarBlank, UsersThree, CurrencyEur, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const BAR_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#a855f7", "#f97316", "#ef4444", "#06b6d4", "#ec4899"];

function Stat({ Icon, color, value, label, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <Icon size={26} weight="bold" className={color} />
      <div className="mt-3 font-head text-3xl font-extrabold tabular-nums">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold">{label}</div>
      <div className="text-muted-foreground">{payload[0].value} activități</div>
    </div>
  );
};

export default function Performance() {
  const { hasRole } = useAuth();
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(false);
  const [target, setTarget] = useState("");
  const canEdit = hasRole("admin", "pm");

  const load = () => api.get("/crm/performance").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveTarget = async () => {
    try { await api.put("/crm/sales-target", { monthly_target: Number(target) || 0 }); toast.success("Obiectiv salvat"); setCfg(false); load(); }
    catch { toast.error("Eroare la salvare"); }
  };

  if (!d) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  const gauge = [{ name: "obiectiv", value: d.objective_pct, fill: "#10b981" }];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="performance-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter">Performanță</h1>
        <p className="mt-1 text-sm text-muted-foreground">Analizează performanța echipei de vânzări</p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat Icon={Phone} color="text-blue-400" value={d.total_calls} label="Total Apeluri" testid="stat-calls" />
        <Stat Icon={EnvelopeSimple} color="text-emerald-400" value={d.total_emails} label="Total Email-uri" testid="stat-emails" />
        <Stat Icon={CalendarBlank} color="text-amber-400" value={d.total_meetings} label="Total Întâlniri" testid="stat-meetings" />
        <Stat Icon={UsersThree} color="text-violet-400" value={d.leads_assigned} label="Lead-uri Atribuite" testid="stat-leads" />
        <Stat Icon={CurrencyEur} color="text-emerald-400" value={fmtMoney(d.revenue)} label="Venit Total" testid="stat-revenue" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6" data-testid="chart-agents">
          <h3 className="mb-4 font-head text-lg font-bold">Activități per Agent</h3>
          {d.activities_per_agent.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Nicio activitate.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.activities_per_agent}>
                <XAxis dataKey="agent" tick={{ fill: "#8b93a5", fontSize: 11 }} axisLine={{ stroke: "#2a2f3a" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#8b93a5", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {d.activities_per_agent.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="chart-objective">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-head text-lg font-bold">Realizare Obiectiv (%)</h3>
            {canEdit && (
              <button onClick={() => { setTarget(String(d.monthly_target)); setCfg(true); }}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="edit-target-btn">
                <PencilSimple size={13} /> Obiectiv lunar
              </button>
            )}
          </div>
          <div className="relative">
            <ResponsiveContainer width="100%" height={280}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={gauge} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background={{ fill: "#1f2937" }} dataKey="value" cornerRadius={12} angleAxisId={0} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-head text-4xl font-extrabold tabular-nums text-emerald-400">{d.objective_pct}%</div>
              <div className="text-xs text-muted-foreground">{fmtMoney(d.revenue)} / {fmtMoney(d.monthly_target)}</div>
            </div>
          </div>
        </div>
      </div>

      {d.per_seller && d.per_seller.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card" data-testid="seller-performance">
          <div className="border-b border-border px-6 py-4">
            <h3 className="font-head text-lg font-bold">Performanță per vânzător</h3>
            <p className="text-xs text-muted-foreground">Vizibil pentru Director Executiv & Director General</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Vânzător</th>
                <th className="px-4 py-3 text-right font-semibold">Lead-uri</th>
                <th className="px-4 py-3 text-right font-semibold">Câștigate</th>
                <th className="px-4 py-3 text-right font-semibold">Activități</th>
                <th className="px-4 py-3 text-right font-semibold">Pipeline</th>
                <th className="px-4 py-3 text-right font-semibold">Venit câștigat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {d.per_seller.map((s) => (
                <tr key={s.id} className="transition-colors hover:bg-white/[0.03]" data-testid={`seller-row-${s.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.role === "asistent_vanzari" ? "Asistent Vânzări" : "Vânzări"}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.leads}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-400">{s.won}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.activities}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{fmtMoney(s.pipeline)}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-emerald-400">{fmtMoney(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={cfg} onOpenChange={setCfg}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Obiectiv lunar de venituri</DialogTitle></DialogHeader>
          <div className="grid gap-1.5">
            <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="ex. 500000" data-testid="target-input" />
            <p className="text-xs text-muted-foreground">Gauge-ul „Realizare Obiectiv" = venit câștigat / acest obiectiv.</p>
          </div>
          <DialogFooter><Button onClick={saveTarget} data-testid="save-target-btn">Salvează</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
